import type { ComponentProps, ReactNode } from "react"

import { cn } from "@/lib/utils"

type Align = "left" | "right" | "center"

const ALIGN: Record<Align, string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
}

export function DataTable({
  minWidth = 780,
  children,
  className,
}: {
  minWidth?: number
  children: ReactNode
  className?: string
}) {
  return (
    <div className="overflow-x-auto">
      <table
        className={cn(
          "w-full border-collapse [&_tr>*:first-child]:pl-0 [&_tr>*:last-child]:pr-0",
          className,
        )}
        style={{ minWidth }}
      >
        {children}
      </table>
    </div>
  )
}

export function Thead({ children }: { children: ReactNode }) {
  return (
    <thead>
      <tr className="border-b border-border">{children}</tr>
    </thead>
  )
}

export function Th({
  align = "left",
  className,
  children,
  ...props
}: ComponentProps<"th"> & { align?: Align }) {
  return (
    <th
      className={cn(
        "h-7 px-2.5 text-micro font-medium uppercase tracking-[0.07em] text-muted-foreground",
        ALIGN[align],
        className,
      )}
      {...props}
    >
      {children}
    </th>
  )
}

export function Tr({ className, ...props }: ComponentProps<"tr">) {
  return (
    <tr
      className={cn(
        "border-b border-border",
        props.onClick && "cursor-pointer",
        "hover:bg-secondary",
        className,
      )}
      {...props}
    />
  )
}

export function Td({
  align = "left",
  muted,
  strong,
  accent,
  num,
  className,
  children,
  ...props
}: ComponentProps<"td"> & {
  align?: Align
  muted?: boolean
  strong?: boolean
  accent?: boolean
  num?: boolean
}) {
  return (
    <td
      className={cn(
        "px-2.5 py-[9px] text-body",
        ALIGN[align],
        num && "num",
        muted && "text-muted-foreground",
        strong && "font-medium",
        accent && "font-medium text-whisper",
        className,
      )}
      {...props}
    >
      {children}
    </td>
  )
}

export function Primary({ title, sub }: { title: ReactNode; sub?: ReactNode }) {
  return (
    <span className="block">
      <span className="block text-body font-medium">{title}</span>
      {sub ? <span className="mt-px block text-meta text-muted-foreground">{sub}</span> : null}
    </span>
  )
}

export function RowList({ children }: { children: ReactNode }) {
  return <div className="flex flex-col">{children}</div>
}

export function Row({
  className,
  as = "div",
  ...props
}: ComponentProps<"div"> & { as?: "div" | "button" }) {
  const Comp = as
  return (
    <Comp
      className={cn(
        "flex w-full items-center gap-3 border-t border-border px-0.5 py-2.5 text-left",
        as === "button" && "cursor-pointer hover:bg-secondary",
        className,
      )}
      {...(props as ComponentProps<"div"> & ComponentProps<"button">)}
    />
  )
}
